import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitService } from '@y42/git';
import { Y42FeatureType } from '@y42/json-schema';
import jsonSchemaPackageJson from '@y42/json-schema/package.json';
import { ToastService } from '@y42/toast';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish } from '@y42/util/nullish';
import findLast from 'lodash/findLast';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { v4 as uuid } from 'uuid';
import { FeatureAggregationService } from '../../../api';
import { Y42GitFolders, Y42GitSourceFolder } from '../../../git';
import { templateFileVersion } from '../template';
import { TemplateFile } from '../template/template-file.model';
import { ImportTemplateNotifierService } from './import-template-notifier.service';
import { ImportTemplateToastMessageComponent } from './import-template-toast-message.component';

@Injectable({ providedIn: 'root' })
export class ImportTemplateService {
  constructor(
    private readonly git: GitService,
    private readonly featureAggregationService: FeatureAggregationService,
    private readonly toastService: ToastService,
    private readonly notifier: ImportTemplateNotifierService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private readonly featureBaseDirectories = Object.values(Y42GitFolders);

  async getTemplateFileContent(file: File): Promise<TemplateFile> {
    const fileContentString = await file.text();

    let templateFileContent: TemplateFile;

    try {
      templateFileContent = JSON.parse(fileContentString as string) as TemplateFile;
    } catch (err) {
      throw new ImportError('Cannot parse the JSON object.');
    }

    const isFileCorrect =
      typeof templateFileContent === 'object' &&
      assertHasProperty(templateFileContent, 'files') &&
      assertHasProperty(templateFileContent, 'mainAsset') &&
      assertHasProperty(templateFileContent, 'schemaVersion') &&
      assertHasProperty(templateFileContent, 'templateFileVersion');

    if (!isFileCorrect) {
      throw new ImportError('Imported JSON object is not a correct template file.');
    }

    if (templateFileContent.schemaVersion !== jsonSchemaPackageJson.version) {
      throw new ImportError(
        `Schema version does not match. Application is expecting version: ${jsonSchemaPackageJson.version}. File version: ${templateFileContent.schemaVersion}.`,
      );
    }

    if (templateFileContent.templateFileVersion !== templateFileVersion) {
      throw new ImportError(
        `Template file version does not match. Application is expecting version: ${templateFileVersion}. File version: ${templateFileContent.templateFileVersion}.`,
      );
    }

    return templateFileContent;
  }

  async importTemplate(templateFile: TemplateFile) {
    try {
      const { integrationsToAuthorize } = await this._importTemplate(templateFile);

      this.toastService.success(ImportTemplateToastMessageComponent, {
        data: { integrationsToAuthorize },
        autoClose: false,
      });
    } catch (err) {
      this.errorHandlerService.error('[ImportTemplateService]: Could not import the template', {
        err,
        toast: 'Could not import the template',
      });
    }
  }

  private async _importTemplate(templateFile: TemplateFile) {
    // eslint-disable-next-line no-restricted-syntax
    console.debug('[Import template] Original asset file: ', templateFile);

    templateFile = this.relinkTemplateWithUniqueFilePaths(templateFile);

    // eslint-disable-next-line no-restricted-syntax
    console.debug('[Import template] Relinked files: ', templateFile.files);

    await this.git.writeFiles(
      Object.entries(templateFile.files).map(
        ([path, content]) => ({ path, content } as { path: string; content: object | string }),
      ),
    );

    const assetName = templateFile.mainAsset.split('/').pop();

    try {
      await this.git.commit(`Imported ${assetName} template.`, Object.keys(templateFile.files));
    } catch (err) {
      // TODO: A common error when pushing multiple files to git (?)
      if (err instanceof Error && err.message === 'Error: Error pushing [-1] - bad packet length') {
        await this.git.pushChanges();
      }
    }

    const servicesToUpdate = this.getServicesToUpdate(Object.keys(templateFile.files));

    for (const service of Array.from(servicesToUpdate)) {
      await service.getAll();
      await service.refreshLastUsed();
    }

    const integrations = uniq(
      Object.keys(templateFile.files)
        .filter((path) => path.match(/^src\/Integrations\/[^/]+\/[^/]+\/settings.json$/))
        .map((path) => path.replace('/settings.json', ''))
        .filter(Boolean),
    );

    const integrationNames = integrations.map((path) => path.match(/^src\/Integrations\/([^/]+)\/[^/]+/)?.[1] ?? '');

    const mainAssetName =
      (templateFile.files[`${templateFile.mainAsset}/metadata.json`] as { name: string } | undefined)?.name ??
      templateFile.mainAsset;

    this.notifier.emitTemplate({ ...templateFile, title: mainAssetName, integrations });

    return { integrationsToAuthorize: integrationNames };
  }

  private relinkTemplateWithUniqueFilePaths(templateFile: TemplateFile): TemplateFile {
    const files = templateFile.files;
    const newFiles = new Map<string, unknown>();
    const assetMapping = new Map<string, string>();

    for (const [filePath, fileContent] of Object.entries(files)) {
      const newFilePath = this.getNewPath(filePath, assetMapping);

      newFiles.set(newFilePath, fileContent);
      assetMapping.set(filePath, newFilePath);
    }

    for (const [filePath, fileContent] of Array.from(newFiles.entries())) {
      newFiles.set(filePath, this.relinkAssets(fileContent, assetMapping));
    }

    return {
      ...templateFile,
      files: Object.fromEntries(newFiles),
      mainAsset: this.getNewPath(templateFile.mainAsset, assetMapping),
    };
  }

  private getNewPath(filePath: string, assetMapping: Map<string, string>) {
    const featureDirectory = findLast(this.featureBaseDirectories, (dir) =>
      filePath.startsWith(`${Y42GitSourceFolder}${dir}`),
    );

    if (!featureDirectory) {
      throw new ImportError(`Cannot find the feature directory of ${filePath}.`);
    }

    const fullFeatureDirectory = `${Y42GitSourceFolder}${featureDirectory}`;

    // Remove the trailing number in parenthesis.
    const match = filePath
      .replace(fullFeatureDirectory, '')
      .slice(1)
      .match(/^[^/]+/);

    assertNonNullable(match);

    const assetId = match[0];

    const originalAssetPath = `${fullFeatureDirectory}/${assetId}`;
    const restPath = filePath.replace(`${originalAssetPath}/`, '');

    const mappedAsset = assetMapping.get(originalAssetPath);

    if (mappedAsset) {
      return filePath.replace(originalAssetPath, mappedAsset);
    }

    // The duplicate form assumption (8 0-9a-f chars after `-`) comes from `get-unique-id-name-pairs` logic.
    const newAssetId = `${assetId.replace(/-[0-9a-f]{8}$/, '')}-${uuid().slice(0, 8)}`;
    const newAssetPath = `${fullFeatureDirectory}/${newAssetId}`;

    assetMapping.set(`${fullFeatureDirectory}/${assetId}`, newAssetPath);

    return `${newAssetPath}/${restPath}`;
  }

  private relinkAssets(jsonPart: unknown, assetMapping: Map<string, string>): unknown {
    // We assume that the asset links other assets only by full paths.
    // False positive values should not be a problem as files not existing
    // in git will be filter out.
    if (typeof jsonPart === 'string' && jsonPart.includes(Y42GitSourceFolder)) {
      // TODO: Performance - a potential bottleneck.
      for (const [originalAsset, newAsset] of assetMapping) {
        jsonPart = (jsonPart as string).replace(new RegExp(`\\b${originalAsset}`), newAsset);
      }

      return jsonPart;
    }

    if (Array.isArray(jsonPart)) {
      return jsonPart.map((innerPart) => this.relinkAssets(innerPart, assetMapping));
    }

    if (typeof jsonPart === 'object' && jsonPart !== null) {
      return Object.fromEntries(
        Object.entries(jsonPart).map(([key, innerPart]) => [key, this.relinkAssets(innerPart, assetMapping)]),
      );
    }

    return jsonPart;
  }

  private getServicesToUpdate(filePaths: string[]) {
    const lineage = this.featureAggregationService.getByFeatureType(Y42FeatureType.Lineage);

    assertNonNullable(lineage);

    return [
      ...uniqBy(
        filePaths.map((filePath) => this.featureAggregationService.getByGitPath(filePath)).filter(isNotNullish),
        (feature) => feature.featureType,
      ),
      // Add lineage as well.
      lineage,
    ].map((feature) => feature.service);
  }
}

function assertHasProperty<T extends object>(object: T, property: keyof T) {
  return property in object;
}

class ImportError extends Error {
  constructor(message: string) {
    super(`[Import template]: ${message}`);
  }
}
